<template>
  <BlockLoading v-if="isPending" />

  <BlockError
    v-else-if="error"
    can-retry
    class="py-6 border rounded"
    @retry="refetch"
  >
    <strong>Error loading charts data</strong>
    <p>{{ error?.message }}</p>
  </BlockError>

  <div
    v-for="(block, index) in blocks"
    v-else
    :key="index"
    :class="['card dashboard-card', block.charts.length === 1 && 'single-chart']"
  >
    <div class="card-header-icon d-flex align-items-center justify-content-between">
      <h2 class="h4 mb-0">{{ block.title }}</h2>
      <ChartPresetSelect
        v-if="index === 0 && enableChartPresets"
        @set-preset="handleSetPreset"
        @clear-preset="handleClearPreset"
      />
      <i v-else :class="`bx bx-${block.icon || 'pie-chart'}`" aria-hidden="true" />
    </div>
    <div class="card-body p-0 d-flex flex-column flex-lg-row align-items-center justify-content-center justify-content-lg-start gap-lg-6 flex-wrap">
      <ChartLegend :items="block.legend" :show-count="block.charts.length === 1" class="p-4" />
      <div
        v-for="chart in block.charts"
        :key="chart.id"
        class="d-flex align-items-center"
        :class="block.charts.length === 1 ? 'mx-auto' : 'ms-lg-auto'"
      >
        <GuChart :options="chart.options" class="p-4" />
        <ChartLegend
          v-if="block.charts.length > 1"
          :items="chart.options.data"
          :show-label="false"
          :show-count="true"
          class="ms-n2 pe-6"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

import BlockError from '@shared/components/BlockError.vue'
import BlockLoading from '@shared/components/BlockLoading.vue'
import GuChart from '@gu-chart/components/GuChart.vue'

import { buildChartBlock } from '@projects/config/projects.dashboard.chart.config'
import ChartLegend from '@projects/components/ChartLegend.vue'
import ChartPresetSelect from '@projects/components/ChartPresetSelect.vue'
import { getChartsData, getChartsDataWithFilters } from '@projects/api/dashboard.api'

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
  enableChartPresets: {
    type: Boolean,
    default: false,
  },
})

const data = ref(null)
const blocks = ref([])

const {
  data: chartsData,
  error,
  isPending,
  refetch,
} = getChartsData()

const {
  mutateAsync: getChartsDataWithFiltersMutation,
} = getChartsDataWithFilters()

watch(chartsData, (newChartsData) => {
  if (newChartsData !== undefined && newChartsData !== null) {
    data.value = newChartsData
  }
}, { immediate: true })

watch(data, (newData) => {
  if (!newData) {
    console.warn('Skipping block build: newData is falsy (likely undefined from mutation)')
    return
  }
  blocks.value = props.config.map(block => buildChartBlock(block, newData))
}, { immediate: true })

async function handleSetPreset (evt) {
  if (evt.agGrid) {
    try {
      const mutationResult = await getChartsDataWithFiltersMutation({ params: { agGrid: evt.agGrid } })
      data.value = mutationResult ?? null
    } catch (err) {
      console.error('Mutation error caught:', err)
      data.value = null
    }
  } else {
    await refetch()
  }
}

async function handleClearPreset () {
  await refetch()
  data.value = chartsData.value
}
</script>

<style lang="scss">
.dashboard-card {
  grid-column: 1 / -1;

  &.single-chart {
    // force blocks with 1 chart to only take 2 columns on large screens
    @media (min-width: 1200px) {
      grid-column: 1 / span 2;
    }
  }

  .gu-chart {
    min-width: 300px;
    max-width: 400px;
    aspect-ratio: 1;
  }
}
</style>
